/**
 * ErrorState Component
 *
 * Reusable error state component with human-readable messages and recovery actions.
 * Supports multiple error types: network, validation, not-found, unauthorized.
 *
 * WCAG AA compliant with proper ARIA labels.
 * Theme-aware with dark/light mode support.
 */

import React from 'react'

import { useTranslation } from '@platform/i18n/hooks/useTranslation'

export type ErrorType =
  | 'network'
  | 'validation'
  | 'not-found'
  | 'unauthorized'
  | 'server'
  | 'generic'

export interface ErrorStateProps {
  /**
   * The type of error. Determines the icon and default messaging.
   */
  type?: ErrorType

  /**
   * Custom error title. If not provided, defaults based on error type.
   */
  title?: string

  /**
   * Custom error message. If not provided, defaults based on error type.
   */
  message?: string

  /**
   * Optional technical details (error code, request id). Printed inline in the compact variant and
   * folded under «Detalles del error» in the full one.
   */
  details?: string

  /**
   * Primary recovery action.
   */
  primaryAction?: {
    label: string
    onClick: () => void
    ariaLabel?: string
  }

  /**
   * Secondary recovery action.
   */
  secondaryAction?: {
    label: string
    onClick: () => void
    ariaLabel?: string
  }

  /**
   * Whether to show a retry button with automatic exponential backoff.
   */
  showRetry?: boolean

  /**
   * Callback for retry action. Required if showRetry is true.
   */
  onRetry?: () => void | Promise<void>

  /**
   * Number of retry attempts (for backoff calculation).
   */
  retryCount?: number

  /**
   * Additional CSS class names.
   */
  className?: string

  /**
   * Compact variant for smaller spaces.
   */
  compact?: boolean
}

/**
 * Per-type icons. The title/message/suggestions text is resolved via i18n
 * INSIDE the component (see below) — icons are static JSX so they stay at
 * module level here, keyed by error type.
 */
const errorIcons: Record<ErrorType, React.ReactNode> = {
  network: (
    <svg
      className="w-16 h-16 text-red-400 [html[data-theme=light]_&]:text-red-700"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
      />
    </svg>
  ),
  validation: (
    <svg
      className="w-16 h-16 text-yellow-400 [html[data-theme=light]_&]:text-yellow-700"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  ),
  'not-found': (
    <svg
      className="w-16 h-16 text-orange-400 [html[data-theme=light]_&]:text-orange-700"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  ),
  unauthorized: (
    <svg
      className="w-16 h-16 text-red-400 [html[data-theme=light]_&]:text-red-700"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
  ),
  server: (
    <svg
      className="w-16 h-16 text-red-400 [html[data-theme=light]_&]:text-red-700"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  generic: (
    <svg
      className="w-16 h-16 text-gray-400 [html[data-theme=light]_&]:text-gray-600"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
}

/**
 * Calculate retry delay with exponential backoff.
 */
function getRetryDelay(retryCount: number): number {
  // Exponential backoff: 2^retryCount * 1000ms, max 30 seconds
  return Math.min(Math.pow(2, retryCount) * 1000, 30000)
}

/**
 * Format delay as human-readable string.
 */
function formatDelay(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${Math.round(ms / 1000)}s`
  return `${Math.round(ms / 60000)}m`
}

/**
 * ErrorState component for displaying errors with recovery actions.
 *
 * @example
 * ```tsx
 * <ErrorState
 *   type="network"
 *   title="Failed to load transactions"
 *   message="Please check your connection and try again."
 *   primaryAction={{ label: "Retry", onClick: handleRetry }}
 *   secondaryAction={{ label: "Refresh", onClick: handleRefresh }}
 * />
 * ```
 */
export const ErrorState: React.FC<ErrorStateProps> = ({
  type = 'generic',
  title,
  message,
  details,
  primaryAction,
  secondaryAction,
  showRetry = false,
  onRetry,
  retryCount = 0,
  className = '',
  compact = false,
}) => {
  // The `errors` namespace is always loaded; bind text resolution to it.
  const { t, i18n } = useTranslation('errors')

  // Suggestions are an array in the locale JSON. The wrapped `t` only ever
  // returns strings (non-string results collapse to the key), so read the array
  // via the raw i18n instance with returnObjects.
  const rawSuggestions: unknown = i18n.t(`errorState.${type}.suggestions`, {
    ns: 'errors',
    returnObjects: true,
  })
  const config = {
    icon: errorIcons[type],
    title: t(`errorState.${type}.title`),
    message: t(`errorState.${type}.message`),
    suggestions: Array.isArray(rawSuggestions) ? (rawSuggestions as string[]) : [],
  }
  const displayTitle = title ?? config.title
  const displayMessage = message ?? config.message

  // Retry state
  const [isRetrying, setIsRetrying] = React.useState(false)
  const [retryDelay] = React.useState(getRetryDelay(retryCount))

  const handleRetry = React.useCallback(async () => {
    if (!onRetry || isRetrying) return

    setIsRetrying(true)
    try {
      await onRetry()
    } finally {
      setIsRetrying(false)
    }
  }, [onRetry, isRetrying])

  // Auto-retry countdown
  React.useEffect(() => {
    if (!showRetry || !isRetrying) return

    const timer = setTimeout(() => {
      handleRetry()
    }, retryDelay)

    return () => clearTimeout(timer)
  }, [showRetry, isRetrying, retryDelay, handleRetry])

  // Countdown for display
  const [countdown, setCountdown] = React.useState(retryDelay)
  React.useEffect(() => {
    if (!showRetry || !isRetrying) return

    const interval = setInterval(() => {
      setCountdown((prev) => Math.max(0, prev - 100))
    }, 100)

    return () => clearInterval(interval)
  }, [showRetry, isRetrying])

  if (compact) {
    return (
      <div
        className={`flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-lg ${className}`}
        role="alert"
        aria-live="assertive"
      >
        <div className="flex-shrink-0">
          <svg
            className="w-5 h-5 text-red-400 [html[data-theme=light]_&]:text-red-700"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-red-300 [html[data-theme=light]_&]:text-red-700">
            {displayTitle}
          </p>
          {displayMessage && (
            <p className="text-sm text-red-400 [html[data-theme=light]_&]:text-red-700/80 mt-0.5">
              {displayMessage}
            </p>
          )}
          {details && (
            <p className="text-xs font-mono text-red-300 [html[data-theme=light]_&]:text-red-700 mt-1 break-all">
              {details}
            </p>
          )}
        </div>
        {(primaryAction ?? showRetry) && (
          <button
            type="button"
            onClick={showRetry ? handleRetry : primaryAction?.onClick}
            disabled={isRetrying}
            className="min-h-[36px] px-3 py-1.5 text-sm bg-red-500 hover:bg-red-600 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={primaryAction?.ariaLabel ?? t('errorState.actions.retry')}
          >
            {isRetrying
              ? t('errorState.actions.retryingIn', { delay: formatDelay(countdown) })
              : (primaryAction?.label ?? t('errorState.actions.retry'))}
          </button>
        )}
      </div>
    )
  }

  return (
    <div
      className={`flex flex-col items-center justify-center text-center py-12 px-6 ${className}`}
      role="alert"
      aria-live="assertive"
    >
      {/* Icon */}
      <div className="mb-4 flex-shrink-0">{config.icon}</div>

      {/* Title */}
      <h3 className="text-lg font-medium text-crypto-text mb-2">{displayTitle}</h3>

      {/* Message */}
      {displayMessage && (
        <p className="text-sm text-crypto-muted max-w-md mb-4">{displayMessage}</p>
      )}

      {/* Details (optional) */}
      {details && (
        <details className="mb-4 text-left">
          <summary className="text-xs text-crypto-muted cursor-pointer hover:text-crypto-text">
            {t('errorState.errorDetails')}
          </summary>
          <pre className="text-xs text-red-400 [html[data-theme=light]_&]:text-red-700 mt-2 p-2 bg-red-500/10 rounded overflow-auto max-w-md">
            {details}
          </pre>
        </details>
      )}

      {/* Suggestions */}
      {!primaryAction && !showRetry && config.suggestions.length > 0 && (
        <ul className="text-sm text-crypto-muted max-w-md mb-6 text-left list-disc list-inside">
          {config.suggestions.map((suggestion, index) => (
            <li key={index}>{suggestion}</li>
          ))}
        </ul>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 flex-wrap justify-center">
        {showRetry && (
          <button
            type="button"
            onClick={handleRetry}
            disabled={isRetrying}
            className="min-h-[44px] px-6 py-2.5 bg-crypto-accent hover:bg-crypto-accent/80 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-crypto-accent focus:ring-offset-2"
            aria-label={t('errorState.actions.retryOperation')}
          >
            {isRetrying
              ? t('errorState.actions.retryingIn', { delay: formatDelay(countdown) })
              : `${t('errorState.actions.retry')}${retryCount > 0 ? ` (${retryCount + 1})` : ''}`}
          </button>
        )}

        {primaryAction && !showRetry && (
          <button
            type="button"
            onClick={primaryAction.onClick}
            disabled={isRetrying}
            className="min-h-[44px] px-6 py-2.5 bg-crypto-accent hover:bg-crypto-accent/80 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-crypto-accent focus:ring-offset-2"
            aria-label={primaryAction.ariaLabel ?? primaryAction.label}
          >
            {primaryAction.label}
          </button>
        )}

        {secondaryAction && (
          <button
            type="button"
            onClick={secondaryAction.onClick}
            className="min-h-[44px] px-6 py-2.5 border border-crypto-border text-crypto-text rounded-lg font-medium transition-colors hover:bg-crypto-card focus:outline-none focus:ring-2 focus:ring-crypto-border focus:ring-offset-2"
            aria-label={secondaryAction.ariaLabel ?? secondaryAction.label}
          >
            {secondaryAction.label}
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * TableErrorState - specialized variant for use in tables.
 */
export interface TableErrorStateProps {
  colSpan: number
  error?: Error | string
  onRetry?: () => void | Promise<void>
  retryCount?: number
  type?: ErrorType
}

export const TableErrorState: React.FC<TableErrorStateProps> = ({
  colSpan,
  error,
  onRetry,
  retryCount = 0,
  type = 'network',
}) => {
  const { t } = useTranslation('errors')
  // Determine error message: prefer the real error text, else a localized default.
  let message = t('errorState.tableDefaultMessage')
  if (typeof error === 'string') {
    message = error
  } else if (error?.message) {
    message = error.message
  }

  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-8">
        <ErrorState
          type={type}
          message={message}
          showRetry={!!onRetry}
          onRetry={onRetry}
          retryCount={retryCount}
          compact
        />
      </td>
    </tr>
  )
}
